type SectionProgressStep = {
  score?: number | null;
  time_accumulator?: number | null;
  completion_date?: string | null;
  step_type?: string | null;
  isSkippedFilter?: boolean | null;
};

/**
 * A step contributes to the section average only when the learner could
 * actually earn a score on it. Theory steps are read, not answered, and a
 * correction step skipped for having nothing to fix never ran — counting
 * either as 0 drags the average below what the learner demonstrated.
 *
 * A recorded step with neither a score nor a completion date was never
 * answered either. That covers the skipped correction whose `isSkippedFilter`
 * flag did not reach the section progress, which otherwise counted as a hard 0.
 */
export function isScorableSectionStep(step: SectionProgressStep): boolean {
  if (step.step_type === 'theory') return false;
  if (step.isSkippedFilter) return false;
  return typeof step.score === 'number' || !!step.completion_date;
}

export function calculateSectionAverageScore(
  steps?: SectionProgressStep[] | null,
): number {
  if (!Array.isArray(steps) || steps.length === 0) return 0;

  const scorableSteps = steps.filter(isScorableSectionStep);
  if (scorableSteps.length === 0) return 0;

  // The average covers what the learner answered, nothing else. Steps they
  // have not reached yet stay out of it — a section is scored on the work
  // done in it, while how much of it is done is what the progress says.
  const total = scorableSteps.reduce((acc, step) => acc + (step.score ?? 0), 0);
  return total / scorableSteps.length;
}

export function calculateSectionAccumulatedTime(
  steps?: SectionProgressStep[] | null,
): number {
  if (!Array.isArray(steps) || steps.length === 0) return 0;

  return steps.reduce((acc, step) => acc + (step.time_accumulator ?? 0), 0);
}
